import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import type { z } from 'zod';

import type {
  DeleteTeamUserByIdResponse,
  GetTeamUsersResponse,
  GetTeamUsersSchema,
  PatchTeamUserByIdResponse,
  UpdateTeamUser,
} from '@mai/platform-api/src/routes/team-users.schema';

import { type QueryOptions, apiClient } from './axios';

// Type definitions from platform API schemas
export type GetTeamUsersRequest = z.infer<typeof GetTeamUsersSchema>;

// API functions
export const fetchTeamUsers = async (
  params?: GetTeamUsersRequest,
): Promise<GetTeamUsersResponse> => {
  const response = await apiClient.get('/team-users', { params });
  return response.data;
};

export const fetchTeamUsersByUserId = async (
  userId: string,
): Promise<GetTeamUsersResponse> => {
  return fetchTeamUsers({ userIds: [userId] });
};

export const deleteTeamUser = async (
  teamUserId: string,
): Promise<DeleteTeamUserByIdResponse> => {
  const response = await apiClient.delete(`/team-users/${teamUserId}`);
  return response.data;
};

export const updateTeamUser = async (
  teamUserId: string,
  data: UpdateTeamUser,
): Promise<PatchTeamUserByIdResponse> => {
  const response = await apiClient.patch(`/team-users/${teamUserId}`, data);
  return response.data;
};

// React Query hooks
export const useTeamUsers = (
  params?: GetTeamUsersRequest,
  options?: QueryOptions,
) => {
  return useQuery({
    queryKey: ['team-users', params],
    queryFn: () => fetchTeamUsers(params),
    enabled: options?.enabled,
    placeholderData: keepPreviousData,
    retry: options?.retry,
  });
};

export const useTeamUsersByUserId = (
  userId: string | null,
  options?: QueryOptions,
) => {
  return useQuery({
    queryKey: ['team-users', 'by-user', userId],
    queryFn: () => fetchTeamUsersByUserId(userId!),
    enabled: !!userId && options?.enabled !== false,
    retry: options?.retry,
  });
};

export const useDeleteTeamUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteTeamUser,
    onSuccess: () => {
      // Invalidate all team-users queries to refetch the data
      void queryClient.invalidateQueries({ queryKey: ['team-users'] });
    },
  });
};

export const useUpdateTeamUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      teamUserId,
      data,
    }: {
      teamUserId: string;
      data: UpdateTeamUser;
    }) => updateTeamUser(teamUserId, data),
    onSuccess: () => {
      // Invalidate all team-users queries to refetch the data
      void queryClient.invalidateQueries({ queryKey: ['team-users'] });
    },
  });
};
